/**
 * HVAC P0: Monthly Payment Calculator
 * Generic utility for all trades
 */

/**
 * Calculate monthly payment using standard loan formula
 * @param {number} principal - Total amount to finance
 * @param {number} apr - Annual percentage rate (e.g., 8.99 for 8.99%)
 * @param {number} termMonths - Term in months
 * @param {number} downPayment - Down payment amount (default: 0)
 * @returns {number} Monthly payment amount
 */
export function calculateMonthlyPayment(principal, apr, termMonths, downPayment = 0) {
  // Handle edge cases
  if (!principal || principal <= 0 || !termMonths || termMonths <= 0) {
    return 0;
  }

  // Calculate amount to finance
  const amountFinanced = principal - (downPayment || 0);

  // If no financing needed
  if (amountFinanced <= 0) {
    return 0;
  }

  // If no interest, just divide by months
  if (!apr || apr === 0) {
    return amountFinanced / termMonths;
  }

  // Calculate monthly interest rate
  const monthlyRate = (apr / 100.0) / 12.0;

  // Standard loan payment formula: P * r * (1+r)^n / ((1+r)^n - 1)
  const payment = amountFinanced * monthlyRate * Math.pow(1 + monthlyRate, termMonths) 
                 / (Math.pow(1 + monthlyRate, termMonths) - 1);

  return Math.round(payment * 100) / 100; // Round to 2 decimal places
}

/**
 * Format monthly payment for display
 * @param {number} payment - Monthly payment amount
 * @returns {string} Formatted string like "$125/mo"
 */
export function formatMonthlyPayment(payment) {
  if (!payment || payment <= 0) {
    return '';
  }

  return `$${Math.round(payment)}/mo`;
}

/**
 * Get financing terms display text
 * @param {number} apr - APR percentage
 * @param {number} termMonths - Term in months
 * @param {number} downPayment - Down payment amount
 * @returns {string} Formatted terms text
 */
export function getFinancingTermsText(apr, termMonths, downPayment = 0) {
  const termYears = Math.round((termMonths / 12) * 10) / 10; // Round to 1 decimal
  const downText = downPayment > 0 ? ` with $${Math.round(downPayment)} down` : '';
  
  return `${apr}% APR for ${termYears} years${downText}. Subject to approval.`;
}

/**
 * Calculate and format monthly payment in one step
 * @param {number} price - Package price
 * @param {Object} financingOptions - Financing options
 * @returns {Object} { monthlyPayment, formattedPayment, termsText }
 */
export function getMonthlyPaymentInfo(price, financingOptions) {
  const { apr, termMonths, downPayment } = financingOptions || {};
  
  const monthlyPayment = calculateMonthlyPayment(price, apr, termMonths, downPayment);
  const formattedPayment = formatMonthlyPayment(monthlyPayment);
  const termsText = getFinancingTermsText(apr, termMonths, downPayment);

  return {
    monthlyPayment,
    formattedPayment,
    termsText
  };
}